<template>
  <v-flex column>
    <v-divider v-show="!firstOrderLine" class="my-2 mx-2" />
     <v-layout column align-end mr-3 style="height: 15px;">
        <div v-for="(price, i) in priceArray" :key="i"
          :set1="discountedAmount = getPriceDiscountedAmount(price)"
          :set2="currency = getCurrency(price)"
          :set3="amount = (price.amount + discountedAmount + modifierAmount) * orderLine.quantity"
          class="text-right"
          :class="{
            'red--text': !orderLine.price.length
          }">
          <v-layout v-if="amount" column :class="{ 'line-through': discountOrderLines.length }" class="bold-text">
            <span 
              v-if="showDisplayCurrency" 
              class="color-text caption" 
              :class="{ 'line-through-grey': discountOrderLines.length }"
            >
              {{ getPriceString({ amount, currency: currency, displayCurrency: displayCurrency }) }}
            </span>
          </v-layout>
        </div>
    </v-layout>    
    <v-layout mx-3>
      <v-layout align-self-start :class="{ 'max-width-190': !isPaid && mdAndUp }">
        <div class="mt-1">
          <amount-badge :amount="quantity" persistent class="mr-2 amount-badge"/>
        </div>
        <v-flex align-self-start>
          <span class="order-line-name">{{ orderLine.name }}</span>
          <v-layout v-show="modifierOptionOrderLines.length" row ml-1 align-center style="margin-top: -4px">
            <template v-for="(orderLine, index) in modifierOptionOrderLines">
              <span :key="index" class="color-text caption">
                {{ orderLine.name }} 
              </span>
              <span :key="`${index}-point`" class="mx-1">
                {{ index +1 !== modifierOptionOrderLines.length ? '·' : ''}} 
              </span> 
            </template>
          </v-layout>
        </v-flex>
      </v-layout>
      <v-layout column align-end justify-start>
        <div v-for="(price, i) in priceArray" :key="i"
          :set1="discountedAmount = getPriceDiscountedAmount(price)"
          :set2="currency = getCurrency(price)"
          :set3="amount = (price.amount + discountedAmount + modifierAmount) * orderLine.quantity"
          class="text-right"
          :class="{
            'red--text': !orderLine.price.length
          }">
          <v-layout v-if="amount" :class="{ 'line-through': discountOrderLines.length }" class="bold-text">
            <span class="amount-text color-text">
              {{ getPriceString({ amount, currency: currency }).split(' ')[0] }}
              <span class="currency-text">
                {{ getPriceString({ amount, currency: currency }).split(' ')[1] }}
              </span>
            </span>
          </v-layout>
          <v-layout v-if="discountedAmount" class="bold-text">
            <span class="color-text">(-{{ getDiscountValue(price) }}%) {{ getPriceString({ amount, currency: currency }) }}</span>
            <span v-if="showDisplayCurrency" class="color-text">
              {{ getPriceString({ amount, currency: currency, displayCurrency: displayCurrency }) }}
            </span>
          </v-layout>
        </div>
        <v-layout style="margin-top: -8px" v-if="!isPaid && orderLine.itemEntity === PRODUCT">
          <span 
            style="text-decoration-line: underline; font-size: 15px;"
            @click="$store.commit('cart/removeProductInCart', { productId: orderLine.itemId, optionIds: modifierOptionOrderLines.map(oL => oL.itemId) })"
            class="clickable primary--text">
            {{ $t("catalog.remove") }}
          </span>
        </v-layout>
      </v-layout>
    </v-layout>
  </v-flex>
</template>

<script>
import { mapState, mapGetters } from 'vuex'
import { orderLineTypes, orderLineItemEntities, currencyTypes } from '@/config'

import moneyMixins from '@/mixins/MoneyFormat'
import vuetifyMixins from '@/mixins/Vuetify'

const { CATALOG_PRODUCT_MODIFIER_OPTION } = orderLineItemEntities

export default {
  props: {
    isPaid: Boolean,
    orderLine: Object,
    orderLines: Array,
    currencies: Array, // Finds currency if not decorated
    firstOrderLine: Boolean,
  },
  mixins: [
    moneyMixins,
    vuetifyMixins,
  ],
  data () {
    return {
      PRODUCT: orderLineItemEntities.PRODUCT,
      LEGAL: currencyTypes.LEGAL,
    }
  },
  computed: {
    ...mapState({
      account: (state) => state.accounts.account,
    }),
    ...mapGetters('me', ['getPriceString', 'showDisplayCurrency', 'displayCurrency']),
    quantity () {
      return this.orderLine.quantity
    },
    discountOrderLines () {
      return this.orderLines ? this.orderLines.filter(oL => {
        return (
          oL.id !== this.orderLine.id &&
          oL.parentOrderLine === this.orderLine.id &&
          oL.orderLineType === orderLineTypes.DISCOUNT
        )
      }) : []
    },
    priceArray () {
      return this.orderLine.price.length ? this.orderLine.price : []
    },
    modifierOptionOrderLines () {
      return this.orderLines.filter(({ parentOrderLine, itemEntity }) => parentOrderLine === this.orderLine.id && itemEntity === CATALOG_PRODUCT_MODIFIER_OPTION)
    },
    modifierAmount () {
      return this.modifierOptionOrderLines.filter(({ includedOnParentLine }) => !includedOnParentLine).reduce((acc, orderLine) => {
        return acc + orderLine.price[0].amount
      }, 0)
    },
  },
  methods: {
    getCurrency (price) {
      return price.currency || (
        this.currencies
          ? this.currencies.find(c => c.id === price.currencyId)
          : this.account.currencies.find((c) => c.id === price.currencyId)
      )
    },
    getPriceDiscountedAmount (price) {
      let discountedAmount = 0
      this.discountOrderLines.forEach((discountOrderLine) => {
        const discountForPriceItem = discountOrderLine.price.find((discountPriceItem) => {
          return discountPriceItem.currencyId === price.currencyId
        })
        if (discountForPriceItem) {
          discountedAmount += Math.abs(discountForPriceItem.amount) * discountOrderLine.quantity
        }
      })
      return discountedAmount
    },
    getDiscountValue (price) {
      let value = 0
      this.discountOrderLines.forEach((discountOrderLine) => {
        const discountForPriceItem = discountOrderLine.price.find((discountPriceItem) => {
          return discountPriceItem.currencyId === price.currencyId
        })
        if (discountForPriceItem) {
          value += Math.abs(discountOrderLine.value)
        }
      })
      return value
    },
  },
  components: {
    AmountBadge: () => import('@/components/view-components/catalog/AmountBadge.vue'),
  },
}
</script>

<style lang="scss" scoped>
.bold-text {
  font-weight: 500;
}
.color-text {
  color: rgba(0, 0, 0, 0.6);
}
.currency-text {
  font-size: 16px;
}
.amount-text {
  font-size: 20px;
}
.order-line-name {
  font-weight: 600;
  font-size: 18px;
  color: rgba(0, 0, 0, 0.6);
}
.line-through-grey {
  text-decoration: line-through;
  text-decoration-color: grey;
}
.max-width-190 {
  max-width: 190px;
}
</style>
